/****************************************************************************
 * Copyright 2018 EPAM Systems
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 ***************************************************************************/
import api from './api'
import molfile from './chem/molfile'
import smiles from './chem/smiles'
import * as structformat from './ui/data/convert/structformat'

import ui from './ui'
import Render from './render'
import graph from './format/chemGraph'

function getSmiles() {
  return smiles
    .stringify(ketcher.editor.struct(), { ignoreErrors: true })
    .replace(/\[sH\]/g, 's')
}
function saveSmiles() {
  const struct = ketcher.editor.struct()
  return structformat
    .toString(struct, 'smiles-ext', ketcher.server)
    .catch(() => smiles.stringify(struct))
}

function getMolfile() {
  return molfile.stringify(ketcher.editor.struct(), { ignoreErrors: true })
}

function setMolecule(molString) {
  if (!(typeof molString === 'string')) return
  molString = molString.replace(/\sR\s/g, ' R1 ')
  ketcher.ui.load(molString, {
    rescale: true
  })
}

function addFragment(molString) {
  if (!(typeof molString === 'string')) return
  molString = molString.replace(/\sR\s/g, ' R1 ')
  ketcher.ui.load(molString, {
    rescale: true,
    fragment: true
  })
}

function showMolfile(clientArea, molString, options) {
  const render = new Render(
    clientArea,
    Object.assign(
      {
        scale: options.bondLength || 75
      },
      options
    )
  )
  if (molString) {
    molString = molString.replace(/\sR\s/g, ' R1 ')
    const mol = molfile.parse(molString)
    render.setMolecule(mol)
  }
  render.update()
  // not sure we need to expose guts
  return render
}

function setMoleculeWithFixedPoints({ molString, selection = {} }) {
  // if (!molString) {
  //   return
  // }
  if (typeof molString === 'string') {
    molString = molString.replace(/\sR\s/g, ' R1 ')
  } else if (typeof molString === 'object') {
    molString = JSON.stringify(molString)
  }
  ketcher.ui.load(
    { molString, selection },
    {
      rescale: true
    }
  )
}

function getAtomsFromBonds(bondsArr) {
  const { bonds: bondsMap } = ketcher.editor.render.ctab
  let res = []
  for (let i = 0; i < bondsArr.length; i++) {
    let bond = bondsMap.get(bondsArr[i])
    if (bond.b) {
      res.push([bond.b.begin, bond.b.end])
    }
  }
  return res
}

function showMolfileWithFixedPoints(
  clientArea,
  { molString, selection = {}, selectColorMap },
  options
) {
  const render = new Render(
    clientArea,
    Object.assign(
      {
        scale: options.bondLength || 75
      },
      options
    )
  )
  if (molString) {
    let struct
    /*
    molString如果是string则当做mol，如果是对象则当做graph
    */
    if (typeof molString === 'string') {
      molString = molString.replace(/\sR\s/g, ' R1 ')
      try {
        struct = molfile.parse(molString)
      } catch (error) {
        return
      }
    } else if (typeof molString === 'object') {
      struct = ketcher.getStructFromGraph(molString)
    }
    render.setMolecule(struct)
    render.ctab.setSelection(selection, selectColorMap)
  }
  setTimeout(() => {
    render.update()
  }, 100)
  // not sure we need to expose guts
  return render
}
// TODO: replace window.onload with something like <https://github.com/ded/domready>
// to start early
export default function init(el, staticResourcesUrl, apiPath) {
  ketcher.apiPath = apiPath
  const params = new URLSearchParams(document.location.search)
  if (params.has('api_path')) ketcher.apiPath = params.get('api_path')
  ketcher.api_path_stone = params.get('api_path_stone')
  ketcher.param_from = params.get('from')
  if (ketcher.param_from === 'stonemind') {
    document.body.classList.add('page-stonemind')
  }
  ketcher.hidden_arr = params.get('hiddenArr')
    ? params.get('hiddenArr').split(',')
    : []
  ketcher.extend_list = params.get('extendList')
    ? params.get('extendList').split(',')
    : []
  ketcher.select_color = params.get('selectColor')
  ketcher.server = api(ketcher.apiPath, {
    'smart-layout': true,
    'ignore-stereochemistry-errors': true,
    'mass-skip-error-on-pseudoatoms': false,
    'gross-formula-add-rsites': true
  })
  // 自定义选中颜色
  const StyleOption = {}
  if (ketcher.select_color) {
    StyleOption.selectionStyle = {
      fill: `#${ketcher.select_color}`,
      stroke: 'none'
    }
  }
  ketcher.ui = ui(
    el,
    staticResourcesUrl,
    Object.assign({}, params, buildInfo, {
      mode: params.get('mode'),
      hoverMode: params.get('hoverMode'),
      ...StyleOption
    }),
    ketcher.server
  )
  ketcher.server.then(
    () => {
      if (params.mol) ketcher.ui.load(params.mol)
    },
    () => {
      document.title += ' (standalone)'
    }
  )
}

const buildInfo = {
  version: process.env.VERSION,
  buildDate: process.env.BUILD_DATE,
  buildNumber: process.env.BUILD_NUMBER || null
}

import validateGraphF from './format/graphValidator' // eslint-disable-line

const ketcher = Object.assign(
  {
    // eslint-disable-line no-multi-assign
    getSmiles,
    saveSmiles,
    getMolfile,
    setMolecule,
    addFragment,
    showMolfile,
    showMolfileWithFixedPoints,
    setMoleculeWithFixedPoints,
    getAtomsFromBonds,
    // TODO: remove it
    toGraph: () => {
      const j = graph.toGraph(ketcher.editor.render.ctab.molecule)
      validateGraphF(j)
      return j
    },
    getStructFromGraph: graphObj => graph.fromGraph(graphObj),
    fromGraph: () =>
      graph.fromGraph(graph.toGraph(ketcher.editor.render.ctab.molecule))
  },
  buildInfo
)

global.ketcher = ketcher
